<template>
  <form @submit.prevent="submit">
    <FormSelect
      name="term"
      v-model="fields.term"
      testId="term-input"
      :label="`${$t('global.term')} *`"
      :errors="validation.fields.term.errors"
      :dirty="validation.fields.term.dirty"
      :disabled="isSubmitting"
      class="pb-4"
    >
      <option v-for="(option, index) in terms" :key="index" :value="option.value">
        {{ option.display }}
      </option>
    </FormSelect>
    <div class="d-flex flex-column align-items-center pb-4">
      <div class="plan-type">{{ applicationName }} {{ packageName }}</div>
      <div class="plan-price">{{ getPrice(fields.term) }} / {{ getTermPeriod(fields.term) }}</div>
    </div>
    <div>
      <p>
        {{ $t("global.plan-change-term-copy", { termFrequency: getTermFrequency(fields.term) }) }}
      </p>
      <ul>
        <li>
          {{ $t("global.plan-change-term-copy-bullet-1", { price: getPrice(fields.term) }) }}
        </li>
        <li>
          {{
            $t("global.plan-change-term-copy-bullet-2", {
              termFrequency: getTermFrequency(fields.term),
            })
          }}
        </li>
        <li>
          {{
            $t("global.plan-change-term-copy-bullet-3", {
              currentTermFrequency: getCurrentTermFrequency(fields.term),
              newTermFrequency: getTermFrequency(fields.term),
              term: fields.term,
              month: getMonth(fields.term),
            })
          }}
        </li>
      </ul>
    </div>
    <div class="form-buttons">
      <Btn
        class="mr-2"
        variant="light"
        type="button"
        :disabled="isSubmitting"
        @click="cancel"
        data-test="cancel-plan-change-term-button"
      >
        {{ $t("global.cancel") }}
      </Btn>
      <Btn
        variant="primary"
        type="submit"
        :disabled="!isSubmittable()"
        :loading="isSubmitting"
        data-test="plan-change-term-button"
      >
        {{
          $t("global.plan-change-term-submit-copy", {
            termFrequency: getTermFrequency(fields.term),
          })
        }}
      </Btn>
    </div>
  </form>
</template>

<script>
import { reactive, ref } from "@vue/composition-api";
import Btn from "@/components/display/Btn";
import FormSelect from "@/components/display/form/FormSelect";
import { moneyFormat } from "leatherman-js/utils/formatters/money";
import store from "@/store";
import useValidator from "@/utils/useValidator";
import { planUpdateTerm as validationRules } from "leatherman-js/utils/validations/forms";

export default {
  name: "PlanUpdateTermForm",
  props: {
    planId: {
      type: Number,
      required: true,
    },
    planTerm: {
      type: Number,
      required: true,
    },
    applicationName: {
      type: String,
      required: true,
    },
    packageName: {
      type: String,
      required: true,
    },
    pricing: {
      type: Object,
      required: true,
    },
  },
  components: {
    Btn,
    FormSelect,
  },
  setup(props, { emit, root }) {
    const terms = ref([
      {
        value: 1,
        display: root.$t("global.monthly"),
      },
      {
        value: 12,
        display: root.$t("global.annually"),
      },
    ]);
    const isSubmitting = ref(false);
    const errors = ref(null);
    const invertTerm = (term) => (Number(term) === 12 ? 1 : 12);
    const fields = reactive({ term: invertTerm(props.planTerm) });
    const { validator } = useValidator();
    const validation = validator(
      fields,
      validationRules,
      {},
      {
        term: {
          required: root.$t("validation.term-required"),
        },
      }
    );
    const getPrice = (term) => moneyFormat(props.pricing[term]);
    const getTermPeriod = (term) =>
      (Number(term) === 12 ? root.$t("global.year") : root.$t("global.month")).toLowerCase();
    const getTermFrequency = (term) =>
      Number(term) === 12 ? root.$t("global.annual") : root.$t("global.monthly");
    const getCurrentTermFrequency = (term) => getTermFrequency(invertTerm(term));
    const getMonth = (term) =>
      (Number(term) > 1 ? root.$t("global.months") : root.$t("global.month")).toLowerCase();

    const cancel = () => emit("close");
    const isSubmittable = () =>
      !isSubmitting.value &&
      validation.errors.length === 0 &&
      Number(fields.term) !== props.planTerm;
    const submit = () => {
      errors.value = null;

      validation
        .validate()
        .then(() => {
          isSubmitting.value = true;
          store
            .dispatch("plans/updateTerm", {
              id: props.planId,
              data: {
                term: fields.term,
              },
            })
            .then(() => {
              emit("close");
            })
            .catch(() => {
              store.dispatch("toasts/addToast", {
                variant: "error",
                message: root.$t("toasts.plan-change-term-error-message"),
              });
            })
            .finally(() => {
              isSubmitting.value = false;
            });
        })
        .catch(() => {});
    };
    return {
      errors,
      fields,
      isSubmitting,
      submit,
      validation,
      cancel,
      isSubmittable,
      getPrice,
      getTermPeriod,
      getTermFrequency,
      getCurrentTermFrequency,
      getMonth,
      terms,
    };
  },
};
</script>

<style lang="scss" scoped>
.plan-type {
  font-size: 1.5rem;
}
.plan-price {
  font-size: 2.5rem;
}
.form-buttons {
  text-align: right;
  padding-top: 2rem;
}
</style>
